// IMPORT React
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../components/CustomFields/TextField";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import {
  CreateVaccine,
  GetVaccineDetail,
  UpdateVaccine,
} from "../../services/VaccineService";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import DraggableList from "../../components/Draggable/DraggableList";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

interface IVaccineDetail {
  vaccineId: number;
  vaccineBrandName: string;
  displayOrder: number;
  isDeleted: boolean;
  mode: string;
}

// SCHEMA FOR FORM
interface formInitialValueTypes {
  vaccineId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  vaccineGenericName: string;
  applyToGender: selectOption | null;
  isSpecial: boolean;
  brandDetailColl: Array<IVaccineDetail>;
  isDeleted: boolean;
  editingBrandIndex: number;
  brandInput: string;
}

const genderOptions = [
  { value: "ALL", label: "All" },
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

const skeletonRowCount = 5;

export default function VaccineSave(Props: any) {
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    brandName,
    vaccineItem,
  } = Props;
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initVaccineData: formInitialValueTypes = {
    vaccineId: 0,
    consumerId: 0,
    branchId: 0,
    orgId: 0,
    vaccineGenericName: isForPopUp ? name || "" : "",
    isSpecial: false,
    applyToGender: { label: "All", value: "ALL" },
    brandDetailColl: [],
    isDeleted: false,
    editingBrandIndex: -1,
    brandInput: "",
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curRawVaccineId = urlParams.get("q");
  let vaccineId = 0;
  // if (curRawVaccineId !== null && curRawVaccineId !== undefined) {
  //   let decId = edService.decrypt(curRawVaccineId);
  //   if (decId !== "" && decId !== null && decId !== undefined && !isForPopUp) {
  //     vaccineId = +decId;
  //   }
  // } else {
  //   if (urlParams.size > 0) {
  //     navigate("/not-found");
  //   }
  // }

  if (!isForPopUp) {
    if (curRawVaccineId !== null && curRawVaccineId !== undefined) {
      let decId = edService.decrypt(curRawVaccineId);
      if (
        decId !== "" &&
        decId !== null &&
        decId !== undefined &&
        !isForPopUp
      ) {
        vaccineId = +decId;
      }
    }
  } else {
    if (isForPopUp && vaccineItem?.vaccineId > 0) {
      vaccineId = vaccineItem?.vaccineId;
    }
  }

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    vaccineId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    vaccineGenericName: Yup.string().required(
      "Vaccine Generic Name is required",
    ),
    applyToGender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender Status is required"),
    isSpecial: Yup.boolean().required(),
    brandDetailColl: Yup.array()
      .of(
        Yup.object().shape({
          displayOrder: Yup.number().required(),
          vaccineId: Yup.number().required(),
          vaccineBrandName: Yup.string().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().nullable(),
        }),
      )
      .test(
        "at-least-one-not-deleted",
        "At least one vaccine brand required",
        (arr) => (arr?.filter((x) => !x.isDeleted)?.length ?? 0) > 0,
      )
      .required(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initVaccineData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const brandInputRef = useRef<HTMLInputElement | null>(null);

  const watchedFields = useWatch({
    control: methods.control,
  });

  const brandDetailColl = watchedFields.brandDetailColl;

  const filteredVaccineBrandDetailColl =
    brandDetailColl?.filter((x) => x?.isDeleted === false && x.mode !== "D") ||
    [];

  const handleVaccineBrandDetailChange = (value: any) => {
    const { dragIndex, hoverIndex } = value;
    const brandDetailColl = methods.getValues().brandDetailColl || [];

    // Clone the array to avoid direct mutation
    const updatedItems = [...brandDetailColl];

    // Get the item at dragIndex
    const draggedItem = updatedItems[dragIndex];

    // Remove the item from its original position (dragIndex)
    updatedItems.splice(dragIndex, 1);

    // Insert the item at hoverIndex
    updatedItems.splice(hoverIndex, 0, draggedItem);

    // Update the displayOrder of each item in the updated array
    const updatedItemsWithDisplayOrder = updatedItems.map((item, index) => {
      return {
        ...item,
        displayOrder: index, // Adjust this if needed to fit your use case
      };
    });

    methods.setValue("brandDetailColl", updatedItemsWithDisplayOrder, {
      shouldValidate: true,
    });
  };

  const handleDeleteVaccineBrandDetail = async (item: any) => {
    const detailColl = methods.getValues().brandDetailColl || [];

    // Mark deleted in local state
    const updatedColl = detailColl?.map((vaccine) => {
      if (vaccine.displayOrder === item.displayOrder) {
        return { ...vaccine, mode: "D", isDeleted: true };
      }
      return vaccine;
    });

    methods.setValue("brandDetailColl", updatedColl, {
      shouldValidate: true,
    });
  };

  const handleAddOrEditVaccineBrand = () => {
    const value = methods.getValues("brandInput")?.trim();
    if (!value) return;

    const detailColl = methods.getValues("brandDetailColl") || [];
    const editIndex = methods.getValues("editingBrandIndex");

    const duplicate = detailColl.some(
      (x, i) =>
        i !== editIndex &&
        x.vaccineBrandName.toLowerCase() === value.toLowerCase(),
    );

    if (duplicate) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Brand already exists.",
        alertType: AlertType.Error,
      });
      return;
    }

    if (editIndex >= 0) {
      const updatedColl = [...detailColl];
      updatedColl[editIndex] = {
        ...updatedColl[editIndex],
        vaccineBrandName: value,
        mode: updatedColl[editIndex].mode === "I" ? "I" : "U",
      };

      methods.setValue("brandDetailColl", updatedColl, {
        shouldValidate: true,
      });
      methods.setValue("editingBrandIndex", -1);
    } else {
      const maxDisplayOrder =
        detailColl.reduce(
          (max, curr) => (curr.displayOrder > max ? curr.displayOrder : max),
          0,
        ) + 1;

      const newItem = {
        vaccineId: 0,
        vaccineBrandName: value,
        displayOrder: maxDisplayOrder,
        isDeleted: false,
        mode: "I",
      };

      methods.setValue("brandDetailColl", [...detailColl, newItem], {
        shouldValidate: true,
      });
    }
    methods.setValue("brandInput", "");
    setTimeout(() => brandInputRef.current?.focus(), 100);
  };

  // GET VACCINE
  const getVaccineDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("vaccineId", vaccineId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetVaccineDetail(params);
      item = res.data.data;
      methods.reset({
        ...initVaccineData,
        vaccineId: item?.vaccineId || 0,
        vaccineGenericName: item?.genericName,
        isSpecial: item?.isSpecial || false,
        applyToGender: item?.applyToGender
          ? genderOptions.find(
              (option) => option.value === item?.applyToGender,
            ) || null
          : null,
        brandDetailColl: item?.vaccineBrandColl || [],
      });
      return item;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE VACCINE
  const saveVaccine = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let vaccineBrandDetail = methods.getValues("brandDetailColl");
      const deepCopy = (obj: any) => JSON.parse(JSON.stringify(obj));
      const detailColl = deepCopy(vaccineBrandDetail || []);
      const saveDetailColl = detailColl || [];
      const detailJson = saveDetailColl?.length > 0 ? saveDetailColl : null;
      let formData = {
        vaccineId: value.vaccineId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        genericName: value.vaccineGenericName?.trim(),
        isSpecial: value.isSpecial || false,
        applyToGender: value.applyToGender?.value,
        VaccineBrandDetail: detailJson ? JSON.stringify(detailJson) : "",
        isDeleted: value.isDeleted,

        dataManagedBy: userContext.loginDetail.userId,
      };
      if (vaccineId > 0) {
        result = await UpdateVaccine(formData);
      } else {
        result = await CreateVaccine(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data && Props.code === "VACCINE_BRAND") {
            handleClosePopUp("VACCINE_BRAND_CREATE", {
              vaccineId: result?.data?.vaccineId ?? result?.vaccineId,
            });
          } else if (result?.data) {
            handleClosePopUp("VACCINE_CREATE", {
              vaccineId: result?.data?.vaccineId ?? result?.vaccineId,
              vaccineGenericName: value.vaccineGenericName?.trim(),
            });
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveVaccine(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (vaccineId > 0) {
        const item = await getVaccineDetail();
      }
      if (brandName) {
        methods.setValue("brandInput", brandName);
        setTimeout(() => brandInputRef.current?.focus(), 100);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [vaccineId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  const VaccineBrandCard = ({ item }: { item: any }) => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          padding: "10px",
          paddingLeft: "20px",
          alignItems: "center",
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Box
              dangerouslySetInnerHTML={{
                __html: item.vaccineBrandName,
              }}
            />
          </Grid>
        </Grid>
        <Tooltip title={"Edit"} placement="top">
          <EditOutlinedIcon
            style={{ cursor: "pointer", color: "#023E8A" }}
            onClick={() => {
              const brandList = methods.getValues("brandDetailColl") || [];
              const index = brandList.findIndex(
                (x) => x.displayOrder === item.displayOrder,
              );
              methods.setValue("brandInput", item.vaccineBrandName);
              methods.setValue("editingBrandIndex", index);
            }}
          />
        </Tooltip>
        <Tooltip title={"Delete"} placement="top">
          <DeleteIcon
            style={{ color: "#d32f2f", cursor: "pointer" }}
            onClick={(e) => {
              handleDeleteVaccineBrandDetail(item);
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box>
      <Box className={"main-container"}>
        {!isForPopUp && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className="group-container">
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.vaccineGenericName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Vaccine Generic Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder={"BCG / HEPATITIS B / RABIES"}
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="vaccineGenericName"
                          size="small"
                          autoFocus={!isForPopUp}
                          {...methods.register("vaccineGenericName", {
                            onChange: (e) => {
                              const upperValue = e.target.value.toUpperCase();
                              e.target.value = upperValue;
                              methods.setValue(
                                "vaccineGenericName",
                                upperValue,
                                { shouldValidate: true },
                              );
                            },
                          })}
                          error={!!methods.formState.errors.vaccineGenericName}
                          className={`text-input ${
                            methods.formState.errors.vaccineGenericName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.vaccineGenericName?.message}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} mt={1}>
                    <FormControlLabel
                      control={
                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-table-header-check-box"
                        >
                          <Checkbox
                            {...methods.register("isSpecial", {
                              onChange: (e) => {
                                methods.setValue(
                                  "isSpecial",
                                  e.target.checked,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              },
                            })}
                            checked={methods?.getValues()?.isSpecial}
                          />
                        </SkeletonWrapper>
                      }
                      label="Is Special Vaccine"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.applyToGender
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Apply to Gender *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="applyToGender"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"applyToGender"}
                              FieldName={"applyToGender"}
                              options={genderOptions}
                              openMenuOnFocus
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.applyToGender?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "applyToGender",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.applyToGender?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <Box
                      className="template-table-container"
                      style={{
                        border: "1px solid #0000001f",
                        borderRadius: "8px",
                      }}
                    >
                      <Box className="inner-title-container">
                        <Box
                          className="template-box"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#e1eaf1",
                            height: "60px",
                          }}
                        >
                          <Typography>Brand(s)</Typography>
                        </Box>
                        {isLoading ? (
                          <>
                            <Grid container spacing={0}>
                              {Array(skeletonRowCount)
                                ?.fill(0)
                                ?.map((skeletonRow, index) => {
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      key={"skeleton-row" + index?.toString()}
                                      sx={{
                                        borderBottom: "1px solid #ced4da",
                                      }}
                                    >
                                      <SkeletonWrapper
                                        isLoading={isLoading}
                                        variant="rectangular"
                                        className="skeleton-draggable-item"
                                      >
                                        <span></span>
                                      </SkeletonWrapper>
                                    </Grid>
                                  );
                                })}
                            </Grid>
                          </>
                        ) : (
                          <DraggableList
                            items={(
                              filteredVaccineBrandDetailColl || []
                            ).filter(
                              (_, i) =>
                                i !== methods.watch("editingBrandIndex"),
                            )}
                            setItem={handleVaccineBrandDetailChange}
                            Styledcard={VaccineBrandCard}
                          />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px",
                            paddingLeft: "20px",
                            flexWrap: "wrap",
                          }}
                        >
                          <FormControl sx={{ flex: 1 }}>
                            <Controller
                              control={methods.control}
                              name="brandInput"
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  size="small"
                                  placeholder="Enter Brand Name"
                                  id="vaccine"
                                  inputRef={brandInputRef}
                                  fullWidth
                                  autoFocus={isForPopUp}
                                />
                              )}
                            />

                            <Typography className="validation-message">
                              {
                                methods.formState.errors.brandDetailColl
                                  ?.message
                              }
                            </Typography>
                          </FormControl>
                          <Tooltip title={"Save"} placement="top">
                            <IconButton
                              style={{ color: "#008632", cursor: "pointer" }}
                              onClick={handleAddOrEditVaccineBrand}
                            >
                              <DoneOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Cancel"} placement="top">
                            <IconButton
                              onClick={() => methods.setValue("brandInput", "")}
                            >
                              <CloseRoundedIcon
                                style={{ color: "#d32f2f", cursor: "pointer" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {vaccineId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
